import {
  ICreateEscalationRequest,
  ICreatePaymentPromiseRequest,
  IEscalationExportQueuedResponse,
  IEscalationParams,
  IEscalationResponse,
  IOpenInvoiceQueryRequest,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';
import { buildNoteRequestBody } from '../noteFormData';
import { CacheTags } from '../../types/cacheTags';

export const escalationApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchEscalations: builder.query<IEscalationResponse, IEscalationParams>({
      query: ({ organisationId, ...params }) => ({
        url: `escalations`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: {
          ...params,
        },
      }),
      providesTags: result => result ?
        [
          ...result.escalations.map(({ id }) => ({ type: CacheTags.Escalations, id })),
          { type: CacheTags.Escalations, id: `LIST` },
        ]
        : [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    createEscalation: builder.mutation<{ id: number }, ICreateEscalationRequest & { organisationId: string }>({
      query: ({ organisationId, ...data }) => ({
        url: `escalations`,
        method: `POST`,
        data,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    exportAllEscalations: builder.mutation<IEscalationExportQueuedResponse, IEscalationParams & { filename?: string; columns?: string[] }>({
      query: ({ organisationId, ...params }) => ({
        url: `escalations/export`,
        method: `POST`,
        params: {
          ...params,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    addNote: builder.mutation<unknown, { organisationId: string; escalationId: number; note: string; expectedPaymentDate?: string; outcome?: string | null; promise?: ICreatePaymentPromiseRequest; query?: IOpenInvoiceQueryRequest; mentionedUsers?: string[]; attachments?: File[]; syncAttachments?: boolean }>({
      query: ({
        organisationId,
        escalationId,
        note,
        expectedPaymentDate,
        outcome,
        promise,
        query,
        mentionedUsers,
        attachments,
        syncAttachments,
      }) => {
        const body = buildNoteRequestBody({ note, expectedPaymentDate, outcome, mentionedUsers }, attachments, syncAttachments);

        // Promises are only sent as JSON (no attachments on that path)
        if (promise && !attachments?.length) {
          body.data = { ...(body.data as Record<string, unknown>), promise };
        }

        if (query && !attachments?.length) {
          body.data = { ...(body.data as Record<string, unknown>), query };
        }

        return {
          url: `escalations/${escalationId}/notes`,
          method: `POST`,
          data: body.data,
          headers: {
            [orgHeader]: organisationId,
            ...body.headers,
          },
        };
      },
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    markCompleteEscalation: builder.mutation({
      query: ({
        organisationId,
        escalationId,
      }) => ({
        url: `escalations/${escalationId}/complete`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    markSnoozeEscalation: builder.mutation({
      query: ({
        organisationId,
        escalationId,
        snoozeDays,
      }) => ({
        url: `escalations/${escalationId}/snooze/${snoozeDays}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    markCancelledEscalation: builder.mutation({
      query: ({
        organisationId,
        escalationId,
      }) => ({
        url: `escalations/${escalationId}/cancel`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    reopenEscalation: builder.mutation({
      query: ({
        organisationId,
        escalationId,
      }) => ({
        url: `escalations/${escalationId}/reopen`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    assignEscalation: builder.mutation({
      query: ({
        organisationId,
        escalationId,
        userId,
      }) => ({
        url: `escalations/${escalationId}/assign/${userId}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    clearAssignedEscalation: builder.mutation({
      query: ({
        organisationId,
        escalationId,
      }) => ({
        url: `escalations/${escalationId}/assign`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    bulkCloseEscalations: builder.mutation({
      query: ({
        organisationId,
        escalationIds,
      }: {
        organisationId: string;
        escalationIds: number[];
      }) => ({
        url: `escalations/bulk/close`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          escalationIds,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    bulkAssignEscalations: builder.mutation<{ success: string }, { organisationId: string; escalationIds: number[]; userId: string | null }>({
      query: ({ organisationId, escalationIds, userId }) => ({
        url: `escalations/bulk/assign`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          escalationIds,
          userId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    bulkSnoozeEscalations: builder.mutation<{ success: string }, { organisationId: string; escalationIds: number[]; snoozeDays: number }>({
      query: ({ organisationId, escalationIds, snoozeDays }) => ({
        url: `escalations/bulk/snooze`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          escalationIds,
          snoozeDays,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
    bulkFreezeEscalations: builder.mutation<{ success: string }, { organisationId: string; escalationIds: number[]; frozen: boolean }>({
      query: ({ organisationId, escalationIds, frozen }) => ({
        url: `escalations/bulk/freeze`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          escalationIds,
          frozen,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Escalations, id: `LIST` },
        { type: CacheTags.Invoices, id: `LIST` },
        { type: CacheTags.Actions, id: `LIST` },
      ],
    }),
    bulkCancelEscalations: builder.mutation({
      query: ({
        organisationId,
        escalationIds,
      }: {
        organisationId: string;
        escalationIds: number[];
      }) => ({
        url: `escalations/bulk/cancel`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          escalationIds,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Escalations, id: `LIST` }],
    }),
  }),
});

export const {
  useFetchEscalationsQuery,
  useCreateEscalationMutation,
  useExportAllEscalationsMutation,
  useAddNoteMutation,
  useMarkCompleteEscalationMutation,
  useMarkSnoozeEscalationMutation,
  useMarkCancelledEscalationMutation,
  useReopenEscalationMutation,
  useAssignEscalationMutation,
  useClearAssignedEscalationMutation,
  useBulkAssignEscalationsMutation,
  useBulkCancelEscalationsMutation,
  useBulkCloseEscalationsMutation,
  useBulkSnoozeEscalationsMutation,
  useBulkFreezeEscalationsMutation,
} = escalationApi;
